<template lang="pug">
#resident-register(v-cloak)
  .box
    .box-header.with-border.register-header
      .title
        span 利用者情報登録

    .box-body.register-body
      FormInput(v-model="name" key-name="利用者名" name="resident-name" type="text" placeholder="利用者名"
        :maxlength="32" :validation-rules="{ required: true, max: 32 }" :styles="{ width: '340px' }" required)
      FormInput(v-model="kanaName" key-name="利用者名（ふりがな）" name="resident-kana-name" type="text" placeholder="利用者名（ふりがな）"
        :maxlength="32" :validation-rules="{ max: 32, hiraganaAndNumbers: true }" :styles="{ width: '340px' }")
      FormInputGender(key-name="性別" v-model="gender")
      FormInputSplitDate(:year.sync="year" :month.sync="month" :day.sync="day"
        key-name="生年月日" validationDateKeyName="生年月日")
      FormInput(v-model="tel" key-name="電話番号" name="tel" type="text" placeholder="電話番号"
        :maxlength="13" :validation-rules="{ tel: true, max: 13 }" :styles="{ width: '340px' }")
      FormInputSelect(v-model="roomId" key-name="部屋" name="room-id"
        :options="roomOptions" :validation-rules="{ required: true }" :styles="{ 'max-width': '540px' }" required)
      FormInputSelect(v-model="groupId" key-name="利用者グループ" name="group-id"
        :options="residentGroupOptions" :validation-rules="{ required: true }" :styles="{ 'max-width': '540px' }" required)
      FormInput(v-model="insuredNumber" key-name="保険者番号" name="insured-number" type="text" placeholder="保険者番号"
        :maxlength="6" :validation-rules="{ length: 6, max: 6, custom_numeric: true }" :styles="{ width: '180px' }")
      FormInput(v-model="careInsuranceInsuredNumber" key-name="介護保険被保険者番号" name="care-insurance-insured-number"
        type="text" placeholder="介護保険被保険者番号" :maxlength="10" :validation-rules="{ length: 10, max: 10, half_alpha_num: true }" :styles="{ width: '320px' }")
      FormDatetimePicker(v-model="inDatetime" :date.sync="inDatetimeDate" :time.sync="inDatetimeTime"
        key-name="入所日／入所予定日" name="in-date" placeholder="入所日／入所予定日")
      FormTimePicker(v-if="inDatetime" v-model="pickUpDatetime" :time.sync="pickUpDatetimeTime"
        key-name="入所送迎時間" name="pick-up-time" placeholder="入所送迎時間")
      FormDatetimePicker(v-model="outDatetime" :date.sync="outDatetimeDate" :time.sync="outDatetimeTime"
        key-name="退所日／退所予定日" name="out-date" placeholder="退所日／退所予定日")
      FormTimePicker(v-if="outDatetime" v-model="dropOffDatetime" :time.sync="dropOffDatetimeTime"
        key-name="退所送迎時間" name="drop-off-time" placeholder="退所送迎時間")
      .register-row(v-show="errorMessage")
        .register-group
          .register-value
            .error-message {{ errorMessage }}

    .box-footer.with-border.register-footer
      ValidationAlert(v-model="showValidationAlert")
      .buttons
        button.btn.register-btn(@click="showConfirmModal()" type="button") 登録
        button.btn.cancel-btn(@click="gotoResidentList()" type="button") キャンセル

  OkCancelModal(v-model="showRegisterModal" title="利用者情報を登録しますか？" @ok="registerResident")
  OkModal(v-model="showRegisterSuccessModal" title="登録に成功しました。" @ok="gotoResidentList")
  
  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";
import { needRefreshesStore } from "@/store/store";
import { ResidentListState, SendMessagesState } from "@/store/NeedRefreshes";

import CustomValidate from "@/commons/utils/CustomValidate";
import { PcUrl } from "@/commons/constants/PcUrl";
import ResidentsService, { ResidentPostModel } from "@/commons/services/Residents";
import ResidentGroupsService, { ResidentGroupsGetModel } from "@/commons/services/ResidentGroups";
import RoomsService, { RoomGetModel, RoomType, UseType } from "@/commons/services/Rooms";
import SystemError from "@/commons/models/SystemError";

import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";
import ValidationAlert from "@/pc/basics/ValidationAlertByValidator.vue";
import FormInput from "@/pc/components/forms/FormInput.vue";
import FormInputGender from "@/pc/components/forms/FormInputGender.vue";
import FormInputSplitDate from "@/pc/components/forms/FormInputSplitDate.vue";
import FormInputSelect from "@/pc/components/forms/FormInputSelect.vue";
import FormDatetimePicker from "@/pc/components/forms/FormDatetimePicker.vue";
import FormTimePicker from "@/pc/components/forms/FormTimePicker.vue";

/**
 * 利用者登録画面コンポーネント
 */
@Component({
  components: {
    OkCancelModal,
    OkModal,
    PageLoading,
    ValidationAlert,
    FormInput,
    FormInputGender,
    FormInputSplitDate,
    FormInputSelect,
    FormDatetimePicker,
    FormTimePicker
  },
  /**
   * #Topic:
   * バリデーターを使いたいコンポーネントは以下を記載してバリデーターをnewする
   */
  $_veeValidate: { validator: "new" }
})
export default class ResidentRegister extends Vue {
  /** 部屋サービス */
  @Inject("RoomsService") roomsService!: RoomsService;
  /** 利用者サービス */
  @Inject("ResidentsService") residentsService!: ResidentsService;
  /** 利用者グループサービス */
  @Inject("ResidentGroupsService") residentGroupsService!: ResidentGroupsService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";
  /** 登録確認モーダル表示可否 */
  showRegisterModal: boolean = false;
  /** 登録完了モーダル表示可否 */
  showRegisterSuccessModal: boolean = false;

  /** バリデーションアラート表示可否 */
  showValidationAlert: boolean = false;

  /** 利用者名（ふりがな） */
  kanaName: string = "";
  /** 利用者名 */
  name: string = "";
  /** 生年月日・年 */
  year: string = "";
  /** 生年月日・月 */
  month: string = "";
  /** 生年月日・日 */
  day: string = "";
  /** 性別 */
  gender: string = "無回答";
  /** 電話番号 */
  tel: string = "";
  /** 部屋ID */
  roomId: number | null = null;
  /** 部屋セレクトボックス用項目 */
  roomOptions: Array<{ value: number | string; label: string }> = [];
  /** グループ */
  groupId: any = null;
  /** グループセレクトボックス用項目 */
  residentGroupOptions: Array<{ value: number | string; label: string }> = [];
  /** 保険者番号 */
  insuredNumber: string = "";
  /** 介護保険被保険者番号 */
  careInsuranceInsuredNumber: string = "";
  /** 入所日 */
  inDatetime: number | null = null;
  /** 入所日(日) */
  inDatetimeDate: string = "";
  /** 入所日(時) */
  inDatetimeTime: string = "";
  /** 入所送迎時間 */
  pickUpDatetime: string | null = null;
  /** 入所送迎時間(時) */
  pickUpDatetimeTime: string = "";
  /** 退所日 */
  outDatetime: number | null = null;
  /** 退所日(日) */
  outDatetimeDate: string = "";
  /** 退所日(時) */
  outDatetimeTime: string = "";
  /** 退所送迎時間 */
  dropOffDatetime: string | null = null;
  /** 退所送迎時間(時) */
  dropOffDatetimeTime: string = "";

  /** エラーメッセージ */
  errorMessage: string = "";

  /**
   * ### vue lifecycle created
   */
  public created(): void {
    CustomValidate.addCustomValidate(this.$validator);
    return;
  }

  /**
   * ### vue lifecycle created
   */
  public async mounted(): Promise<void> {
    await this.createView();
    return;
  }

  /**
   * ### 画面情報作成
   *
   * - 部屋の選択肢を作成する
   * - 利用者グループの選択肢を作成する
   * - 入所日の初期値を作成する
   * - 入所送迎時間の初期値を作成する
   * - 退所予定日の初期値を作成する
   * - 退所送迎時間の初期値を作成する
   */
  public async createView(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    const rooms: Array<RoomGetModel> = await this.getRooms();
    const roomOptions: Array<{ value: number; label: string }> = this.createRoomOptions(rooms);
    this.roomOptions = roomOptions;

    const residentGroups: Array<ResidentGroupsGetModel> = await this.residentGroupsService.gets(userStore);
    const residentGroupOptions: Array<{ value: number; label: string }> = this.createResidentGroupOptions(residentGroups);
    this.residentGroupOptions = residentGroupOptions;

    const inDatetimeDate: string = this.$dateUtil.convertDateToString(new Date(), "yyyy-mm-dd");
    const inDatetimeTime: string = "10:00:00";
    this.inDatetimeDate = inDatetimeDate;
    this.inDatetimeTime = inDatetimeTime;
    this.pickUpDatetimeTime = "10:00:00";

    const outDatetimeDate: string = this.$dateUtil.convertDateToString(new Date(), "yyyy-mm-dd");
    const outDatetimeTime: string = "10:00:00";
    this.outDatetimeDate = outDatetimeDate;
    this.outDatetimeTime = outDatetimeTime;
    this.dropOffDatetimeTime = "10:00:00";

    this.isBusy = false;

    return;
  }

  /**
   * ### 部屋取得
   */
  public async getRooms(): Promise<Array<RoomGetModel>> {
    const response: Array<RoomGetModel> = await this.roomsService.gets(userStore);
    return response;
  }

  public createRoomOptions(rooms: Array<RoomGetModel>): Array<{ value: number; label: string }> {
    let roomOptions: Array<{ value: number; label: string }> = [];
    for (const room of rooms) {
      roomOptions.push({
        value: room.id,
        label: `${room.name}（${this.getUseTypeName(room.useType)}・${this.getRoomTypeName(room.roomType)}）`
      });
    }

    return roomOptions;
  }

  public getUseTypeName(useType: number): string {
    if (useType == UseType.UNMANAGED) {
      return "空床管理対象外";
    } else if (useType == UseType.NORMAL_STAY) {
      return "入居用部屋";
    } else if (useType == UseType.SHORT_STAY) {
      return "ショートステイ";
    } else {
      return "";
    }
  }

  public getRoomTypeName(roomType: number): string {
    if (roomType == RoomType.UNMANAGED) {
      return "空床管理対象外";
    } else if (roomType == RoomType.SINGLE) {
      return "個室";
    } else if (roomType == RoomType.DOUBLE) {
      return "２人部屋（夫婦部屋）";
    } else if (roomType == RoomType.MULTI_MEN) {
      return "多床室（男性）";
    } else if (roomType == RoomType.MULTI_WOMEN) {
      return "多床室（女性）";
    } else {
      return "";
    }
  }

  /**
   * ### グループセレクトボックス用項目を作成する
   *
   * - アカウントの権限に応じて選択肢をマスキングする
   */
  public createResidentGroupOptions(residentGroups: Array<ResidentGroupsGetModel>): Array<{ value: number; label: string }> {
    const residentGroupOptions: Array<{ value: number; label: string }> = [];

    const allowedGroupsMap: Map<number, number> = new Map<number, number>();
    for (const allowedGroup of userStore.loginUser.permissions.residentGroups) {
      allowedGroupsMap.set(allowedGroup, allowedGroup);
    }
    for (const group of residentGroups) {
      if (allowedGroupsMap.has(group.id)) {
        residentGroupOptions.push({
          value: group.id,
          label: group.name
        });
      }
    }

    return residentGroupOptions;
  }

  /**
   * ### 登録確認モーダルを表示する
   *
   * - 表示前にバリデーションチェックして、エラーならモーダルは表示しない
   */
  public async showConfirmModal(): Promise<void> {
    const validationResult: boolean = await this.$validator.validateAll();
    const birthdayResult: boolean = await this.validateBirthday();
    const inDateTimeValidationResult: boolean = await this.validateInDateTime(); // validateAll()より後に実行しないと動かないので注意

    if (validationResult && birthdayResult && inDateTimeValidationResult) {
      this.showValidationAlert = false;
      this.showRegisterModal = true;
    } else {
      this.showValidationAlert = true;
    }

    return;
  }

  /** 日付の不正チェックは自前で行う */
  private async validateBirthday(): Promise<boolean> {
    this.$validator.errors.remove("date");
    const birthday: number | null = this.joinBirthday();

    if (birthday) {
      const { valid, errors } = await this.$validator.verify(birthday, "exist_date", { name: "生年月日", bails: false });
      if (!valid) {
        for (const error of errors) {
          this.$validator.errors.add({ field: "date", msg: error });
        }
      }
      return valid;
    }
    return true;
  }

  /**
   * ### 分割している生年月日を結合する
   */
  private joinBirthday(): number | null {
    if (this.year || this.month || this.day) {
      // 月と日が1桁の場合は0埋めする
      let month: string = String(this.month);
      if (month.length == 1) {
        month = `0${month}`;
      }
      // 1桁の場合は0埋めする
      let day: string = String(this.day);
      if (day.length == 1) {
        day = `0${day}`;
      }

      return Number(`${this.year}${month}${day}000000000`);
    }

    return null;
  }

  /** 入所日時・退所日時のチェックは自前で行う */
  public async validateInDateTime(): Promise<boolean> {
    this.$validator.errors.remove("in-date");
    if (this.inDatetime && this.outDatetime) {
      if (this.outDatetime < this.inDatetime) {
        this.$validator.errors.add({ field: "in-date", msg: "入所日が退所日より未来です" });
        return false;
      } else {
        return true;
      }
    }
    return true;
  }

  /**
   * ### 利用者登録
   */
  public async registerResident(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "登録中...";

    try {
      const requestBody: ResidentPostModel = this.createRequestBody();
      await this.residentsService.post(userStore, requestBody);

      // 利用者一覧を再読み込みさせるようstoreを更新
      let residentList: ResidentListState = needRefreshesStore.residentList;
      residentList.residentList = true;
      needRefreshesStore.setResidentList(residentList);
      // メッセージ送信の宛先が変更になるので再読み込みさせるようstoreを更新
      let sendMessageState: SendMessagesState = needRefreshesStore.sendMessage;
      sendMessageState.sendMessages = true;
      needRefreshesStore.setSendMessage(sendMessageState);

      // 登録成功ダイアログを表示する
      this.showRegisterSuccessModal = true;
    } catch (error) {
      // 利用者数の上限に達している場合、ステータスコード422が返却される
      // その場合はエラーメッセージを表示する
      // ステータスコード422以外は例外を投げなおして共通エラーハンドラに処理を委譲する
      const systemError: SystemError = error as SystemError;
      if (systemError.statusCode == 422) {
        this.errorMessage = "ご契約プランの利用者登録数上限に達しています。\n新規に追加する場合は設定画面からプラン変更もしくは利用者情報を削除してから行ってください。";
      } else {
        throw error;
      }
    } finally {
      this.isBusy = false;
    }

    return;
  }

  /**
   * ### 利用者登録パラメータ作成
   */
  public createRequestBody(): ResidentPostModel {
    // 有効期限Toは現在年月日から50年後までを範囲とする
    let date = new Date();
    date.setFullYear(date.getFullYear() + 50);

    // 生年月日は分割されているので結合する
    const birthday: number | null = this.joinBirthday();

    // 入所送迎時間、退所送迎時間は入所日と退所送迎日の日付を使用する
    let pickUpDatetime: number | null = null;
    if (this.inDatetime && this.pickUpDatetime) {
      const pickUpDate: string = this.$dateUtil.convertNumberToString(this.inDatetime, "yyyymmdd");
      const pickUpTime: string = this.$dateUtil.replaceNonNumeric(this.pickUpDatetime); // time format is hh:MM:ss
      pickUpDatetime = Number(`${pickUpDate}${pickUpTime}000`);
    }
    let dropOffDatetime: number | null = null;
    if (this.outDatetime && this.dropOffDatetime) {
      const dropOffDate: string = this.$dateUtil.convertNumberToString(this.outDatetime, "yyyymmdd");
      const dropOffTime: string = this.$dateUtil.replaceNonNumeric(this.dropOffDatetime); // time format is hh:MM:ss
      dropOffDatetime = Number(`${dropOffDate}${dropOffTime}000`);
    }

    // 入所・退所情報が1つでもあればそれを配列に格納し、なければ空配列を格納する
    let inAndOutDatetimes = [];
    if (this.inDatetime || this.outDatetime || pickUpDatetime || dropOffDatetime) {
      inAndOutDatetimes.push({
        inDatetime: this.inDatetime ? this.inDatetime : null,
        pickUpDatetime: pickUpDatetime ? pickUpDatetime : null,
        outDatetime: this.outDatetime ? this.outDatetime : null,
        dropOffDatetime: dropOffDatetime ? dropOffDatetime : null,
        roomId: this.roomId as number
      });
    }

    // 登録用利用者を作成
    const resident: ResidentPostModel = {
      kanaName: this.kanaName ? this.$stringUtil.convertYen(this.kanaName) : "",
      name: this.$stringUtil.convertYen(this.name),
      birthday: birthday,
      gender: this.gender,
      tel: this.tel,
      groupId: this.groupId,
      roomId: this.roomId ? this.roomId : null,
      inAndOutDatetimes: inAndOutDatetimes,
      insuredNumber: this.insuredNumber ? this.insuredNumber : null,
      careInsuranceInsuredNumber: this.careInsuranceInsuredNumber ? this.careInsuranceInsuredNumber : null
    };

    return resident;
  }

  /**
   * ### 利用者一覧に遷移する
   */
  public gotoResidentList(): void {
    this.$router.push(PcUrl.ResidentList);

    return;
  }
}
</script>

<style lang="scss" scoped>
#resident-register {
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;

  .register-btn,
  .cancel-btn {
    width: 120px;
  }
}
</style>
